import type { AcceptanceCriterion, Priority, RiskFlag, Size, TaskKind } from "../types.ts";

export const WORKER_KEYS = ["testgaps", "audit", "document", "map"] as const;
export type WorkerKey = (typeof WORKER_KEYS)[number];

export type PathKind = "test" | "code" | "doc" | "other";

export type ChangedFile = { path: string; added: number; deleted: number; kind: PathKind };
export type UndocumentedExport = { path: string; line: number; name: string };

/** What a merge actually changed on main, collected by the node (best-effort, capped). */
export type MergeChanges = {
  files: ChangedFile[];
  undocumentedExports: UndocumentedExport[];
  /** True when the node capped the file or export lists. */
  truncated: boolean;
  /** Nearest package.json directory for the changed files, found by the node (spec Clarification 3). */
  packageRoots?: string[];
};

/** Commits touching one file over the churn window (30 days), from the node's git snapshot. */
export type ChurnEntry = { path: string; commits: number; lastAt: number };

export type WorkerConfig = {
  /** testgaps: minimum added lines of code before a missing test counts. */
  minAddedLines: number;
  /** Max tasks one worker files per project per UTC day. */
  dailyCap: number;
  /** audit: path globs that trigger a security review. */
  paths: string[];
  /** document: path globs never reported. */
  ignore: string[];
  /** testgaps: package roots (directories) used to scope the filed test areas. */
  packageRoots: string[];
};

export const DEFAULT_AUDIT_PATHS = ["**/auth/**", "**/payments/**", "**/billing/**", "**/security/**", "**/*sandbox*"];
export const DEFAULT_DOCUMENT_IGNORE = ["**/*.test.*", "**/*.spec.*", "**/test/**", "**/tests/**", "**/__tests__/**"];

/** How far back "filed in 7d" looks. The board's `since=7d` uses the same window. */
export const WORKER_FILED_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

export type WorkerFilingTask = { labels: readonly string[]; createdAt: number; stage: string };

/** Inclusive `createdAt` cutoff for a filing counted at `now`. */
export function workerFilingCutoff(now: number): number {
  return now - WORKER_FILED_WINDOW_MS;
}

/**
 * A task the Workers card counts for `worker` and the board shows for
 * `?label=worker:<name>&since=<cutoff>` with done visible: that label, created inside the window,
 * cancelled left out.
 */
export function isWorkerFiling(task: WorkerFilingTask, worker: string, now: number): boolean {
  return task.stage !== "cancelled" && task.createdAt >= workerFilingCutoff(now) && task.labels.includes(`worker:${worker}`);
}

/** `isWorkerFiling` counted across `tasks`. */
export function countWorkerFilings(tasks: readonly WorkerFilingTask[], worker: string, now: number): number {
  let n = 0;
  for (const task of tasks) if (isWorkerFiling(task, worker, now)) n += 1;
  return n;
}

export const DEFAULT_WORKER_CONFIG: WorkerConfig = {
  minAddedLines: 10,
  dailyCap: 5,
  paths: DEFAULT_AUDIT_PATHS,
  ignore: DEFAULT_DOCUMENT_IGNORE,
  packageRoots: [],
};

/** Task fields a decide function reads; structurally a subset of `RoutableTask`. */
export type WorkerSourceTask = { id: string; number: number; projectId: string; labels: string[] };

/** Structurally the server's `CreateTaskInput`. */
export type WorkerTaskInput = {
  projectId: string;
  title: string;
  description?: string;
  kind?: TaskKind;
  acceptanceCriteria?: (AcceptanceCriterion | string)[];
  areas?: string[];
  labels?: string[];
  skills?: string[];
  size?: Size;
  flags?: RiskFlag[];
  priority?: Priority;
};

export type WorkerDecision = { file: WorkerTaskInput } | { skip: string } | null;

export type MapOwner = { actorId: string; name: string; merged: number };
export type MapArea = { area: string; owners: MapOwner[]; allowedAgents: { id: string; name: string }[] };
export type HotFile = { path: string; commits: number; lastAt: number };
export type CodebaseMap = { builtAt: number; areas: MapArea[]; hotFiles: HotFile[] };

export type MergedTaskInfo = { id: string; number: number; areas: string[]; builderId: string | null; mergedAt: number };
export type MapActor = { id: string; name: string; kind: "human" | "agent"; allowedAreas: string[] };

/** Stored worker settings: `WorkerConfig` plus whether filed tasks route on their own. */
export type WorkerSettingConfig = WorkerConfig & {
  /** true routes filed tasks normally; false leaves them in backlog for a person. */
  autoRoute: boolean;
};

/** One background worker's settings for a project (GET /projects/:id/workers). */
export type WorkerSettingDTO = {
  worker: WorkerKey;
  enabled: boolean;
  enabledBy: string | null;
  enabledAt: number | null;
  config: WorkerSettingConfig;
  /** Latest worker_runs row (map: latest build), null when it never ran. */
  lastRunAt: number | null;
  /** `countWorkerFilings` for this worker. */
  filed7d: number;
  /** Inclusive `createdAt` cutoff `filed7d` used (hub clock). The board link passes this as `since`. */
  filedSince: number;
  /**
   * Why the hub turned this worker off (Clarification 8). Null while it is on, and after a later
   * toggle that turns it on. A config change that leaves it off keeps the reason. Empty string
   * means it was turned off with no reason text. Stored on `project_workers.disabled_reason`.
   */
  disabledReason: string | null;
};
